/**
 * DOM diffing utility.
 */
export default class DomDiff {
	/**
	 * Renders the DOM.
	 *
	 * @param fromNode From Node.
	 * @param toNode To Node.
	 */
	public static applyDiff(fromNode: Element | ShadowRoot, toNode: Element): void {
		this.removeWhiteSpaceNodes(toNode.childNodes);

		const fromChildren = Array.from(fromNode.childNodes);
		const toChildren = Array.from(toNode.childNodes);

		for (let i = 0, max = toChildren.length; i < max; i++) {
			if (i < fromChildren.length) {
				if (this.nodeTypeChanged(<Element>fromChildren[i], <Element>toChildren[i])) {
					this.replaceNode(fromChildren[i], toChildren[i]);
				} else {
					this.setNodeAttributes(fromChildren[i], toChildren[i]);
					this.setNodeValue(fromChildren[i], toChildren[i]);
					this.applyDiff(<Element>fromChildren[i], <Element>toChildren[i]);
				}
			} else {
				fromNode.appendChild(toChildren[i]);
				if (toChildren[i].nodeType === Node.ELEMENT_NODE) {
					this.evaluateCode(this.getScriptCode(<Element>toChildren[i]));
				}
			}
		}

		for (let i = toChildren.length, max = fromChildren.length; i < max; i++) {
			fromChildren[i].parentNode?.removeChild(fromChildren[i]);
		}
	}

	/**
	 * Checks if a node type has changed.
	 *
	 * @param fromNode From Node.
	 * @param toNode To Node.
	 * @returns If node has changed.
	 */
	private static nodeTypeChanged(fromNode: Element, toNode: Element): boolean {
		return fromNode.nodeType !== toNode.nodeType || fromNode.tagName !== toNode.tagName;
	}

	/**
	 * Replaces a node.
	 *
	 * @param fromNode From Node.
	 * @param toNode To Node.
	 */
	private static replaceNode(fromNode: Node, toNode: Node): void {
		fromNode.parentNode?.replaceChild(toNode, fromNode);
		if (toNode.nodeType === Node.ELEMENT_NODE) {
			this.evaluateCode(this.getScriptCode(<Element>toNode));
		}
	}

	/**
	 * Updates node attributes.
	 *
	 * @param fromNode From Node.
	 * @param toNode To Node.
	 */
	private static setNodeAttributes(fromNode: Node, toNode: Node): void {
		if (fromNode instanceof Element) {
			const childrenAttr: string[] = [];

			for (let a = 0, max = fromNode.attributes.length; a < max; a++) {
				childrenAttr.push(fromNode.attributes[a].name);
			}

			if (toNode instanceof Element) {
				for (let a = 0, max = toNode.attributes.length; a < max; a++) {
					const attr = toNode.attributes[a].name;
					const value = toNode.attributes[a].value;

					if (fromNode.getAttribute(attr) !== value) {
						this.setAttribute(fromNode, attr, value);
					}

					const indexOf = childrenAttr.indexOf(attr);

					if (indexOf !== -1) {
						childrenAttr.splice(indexOf, 1);
					}
				}

				for (const attr of childrenAttr) {
					this.removeAttribute(fromNode, attr);
				}
			}
		}
	}

	/**
	 * Updates node value.
	 *
	 * @param fromNode From Node.
	 * @param toNode To Node.
	 */
	private static setNodeValue(fromNode: Node, toNode: Node): void {
		if (fromNode.nodeValue !== toNode.nodeValue) {
			fromNode.nodeValue = toNode.nodeValue;
			if (
				toNode.nodeValue &&
				toNode.parentNode &&
				(<Element>toNode.parentNode).tagName === 'SCRIPT'
			) {
				this.evaluateCode(toNode.nodeValue);
			}
		}
	}

	/**
	 * Sets attribute on an element.
	 *
	 * @param element Element.
	 * @param attr Attribute name.
	 * @param value Attribute value.
	 */
	private static setAttribute(element: Element, attr: string, value: string): void {
		// Handle selected value in select box
		if (
			element instanceof HTMLOptionElement &&
			element.parentNode &&
			element.parentNode instanceof HTMLSelectElement &&
			attr === 'selected'
		) {
			element.parentNode.selectedIndex = Array.from(element.parentNode.options).indexOf(element);
		}

		// Handle selected value in input
		if (element.tagName && element instanceof HTMLInputElement) {
			if (attr === 'value') {
				element.value = value;
			} else if (element.type === 'checkbox' && attr === 'checked') {
				element.checked = true;
			}
		}

		element.setAttribute(attr, value);
	}

	/**
	 * Removes an attribute on an element.
	 *
	 * @param element Element.
	 * @param attr Attribute name.
	 */
	private static removeAttribute(element: Element, attr: string): void {
		element.removeAttribute(attr);

		// Handle selected value in input
		if (element.tagName && element instanceof HTMLInputElement) {
			if (attr === 'value') {
				element.value = '';
			} else if (element.type === 'checkbox' && attr === 'checked') {
				element.checked = false;
			}
		}
	}

	/**
	 * Removes white space nodes from a NodeList.
	 *
	 * @param nodeList Nodes.
	 */
	private static removeWhiteSpaceNodes(nodeList: NodeList): void {
		const nodes = Array.from(nodeList);

		for (let i = 0, max = nodes.length; i < max; i++) {
			const isWhiteSpace =
				nodes[i].nodeType === Node.TEXT_NODE && nodes[i].nodeValue?.replace(/\s+/gm, '') === '';

			if (!nodes[i].parentNode || isWhiteSpace) {
				if (nodes[i].parentNode && isWhiteSpace) {
					nodes[i].parentNode?.removeChild(nodes[i]);
				}
			} else {
				this.removeWhiteSpaceNodes(nodes[i].childNodes);
			}
		}
	}

	/**
	 * Evaluates script code.
	 *
	 * @param code Code.
	 */
	private static evaluateCode(code: string | null): void {
		if (code) {
			eval(code);
		}
	}

	/**
	 * Returns script code.
	 *
	 * @param element Element.
	 * @returns Script code.
	 */
	private static getScriptCode(element: Element): string | null {
		if (element.tagName === 'SCRIPT') {
			const content = element.textContent?.trim();
			if (content) {
				return content;
			}
		} else {
			const scripts = Array.from(element.querySelectorAll('script'));
			let code = '';
			for (const script of scripts) {
				const content = script.textContent?.trim();
				if (content) {
					code += content;
				}
			}
			return code;
		}

		return null;
	}
}
